import { AgDialog, SingleAutocomplete } from '@australiangreens/ag-internal-components';
import { Button, DialogContentText, TextField } from '@mui/material';

import { adjustPageNo } from '@/util';
import { EditTemplateDialog } from '@components/EditTemplateDialog';
import LoadSearchTemplateDialog from '@components/LoadSearchTemplateDialog';
import TemplateLoadErrorDialog from '@components/TemplateLoadErrorDialog';
import SearchTemplatesTable from '@pages/SavedSearchesPage/SearchTemplatesTable';
import { useSavedSearchesPage } from './useSavedSearchesPage';

type TemplatesTabProps = Pick<
  ReturnType<typeof useSavedSearchesPage>,
  | 'lookupUsers'
  | 'searchTemplateName'
  | 'searchTemplateDescription'
  | 'searchTemplateCreatedBy'
  | 'handleSearchTemplateNameChange'
  | 'handleSearchTemplateDescriptionChange'
  | 'handleSearchTemplateCreatedByChange'
  | 'handleResetSearchTemplateFilter'
  | 'isInitialSearchTemplateLoading'
  | 'mySearchTemplatesMapped'
  | 'searchTemplatePageNoAtomModel'
  | 'searchTemplatePageSizeAtomModel'
  | 'setSearchTemplatePageNoAtomModel'
  | 'setSearchTemplatePageSizeAtomModel'
  | 'searchTemplateColumnVisibilityAtomModel'
  | 'setSearchTemplateColumnVisibilityAtomModel'
  | 'searchTemplateSortAtomModel'
  | 'setSearchTemplateSortAtomModel'
  | 'setSearchTemplateForDelete'
  | 'setParametersForTemplateEdit'
  | 'handleTemplateUpdate'
  | 'handleTemplateCopy'
  | 'activateCopyLink'
  | 'searchTemplateForDelete'
  | 'handleDeleteSearchTemplateDialogDelete'
  | 'searchTemplateIdEdit'
  | 'setSearchTemplateIdEdit'
  | 'searchTemplateNameEdit'
  | 'setSearchTemplateNameEdit'
  | 'searchTemplateDescriptionEdit'
  | 'setSearchTemplateDescriptionEdit'
  | 'originalSearchTemplateName'
  | 'searchTemplateForLoad'
  | 'handleCloseLoadTemplateModal'
  | 'templateLoadError'
  | 'handleCloseTemplateLoadError'
>;

export default function TemplatesTab({
  lookupUsers,
  searchTemplateName,
  searchTemplateDescription,
  searchTemplateCreatedBy,
  handleSearchTemplateNameChange,
  handleSearchTemplateDescriptionChange,
  handleSearchTemplateCreatedByChange,
  handleResetSearchTemplateFilter,
  isInitialSearchTemplateLoading,
  mySearchTemplatesMapped,
  searchTemplatePageNoAtomModel,
  searchTemplatePageSizeAtomModel,
  setSearchTemplatePageNoAtomModel,
  setSearchTemplatePageSizeAtomModel,
  searchTemplateColumnVisibilityAtomModel,
  setSearchTemplateColumnVisibilityAtomModel,
  searchTemplateSortAtomModel,
  setSearchTemplateSortAtomModel,
  setSearchTemplateForDelete,
  setParametersForTemplateEdit,
  handleTemplateUpdate,
  handleTemplateCopy,
  activateCopyLink,
  searchTemplateForDelete,
  handleDeleteSearchTemplateDialogDelete,
  searchTemplateIdEdit,
  setSearchTemplateIdEdit,
  searchTemplateNameEdit,
  setSearchTemplateNameEdit,
  searchTemplateDescriptionEdit,
  setSearchTemplateDescriptionEdit,
  originalSearchTemplateName,
  searchTemplateForLoad,
  handleCloseLoadTemplateModal,
  templateLoadError,
  handleCloseTemplateLoadError,
}: TemplatesTabProps) {
  return (
    <>
      <div
        style={{
          display: 'flex',
          paddingLeft: '12px',
          paddingRight: '12px',
          marginBottom: '16px',
          gap: '16px',
        }}
      >
        <div>
          <TextField
            id="search-template-textfield-name"
            label={'Template name'}
            type="text"
            variant="outlined"
            value={searchTemplateName}
            onChange={handleSearchTemplateNameChange}
            color="primary"
            sx={{ width: '260px' }}
            autoComplete="off"
          />
        </div>
        <div>
          <SingleAutocomplete
            data-testid="selectTemplateCreatedBy"
            onChange={handleSearchTemplateCreatedByChange}
            value={searchTemplateCreatedBy}
            label={'Created by'}
            lookup={lookupUsers}
            noOptionsText="No users found"
            minLength={3}
            sx={{ width: '220px' }}
            textFieldColor="primary"
            textFieldVariant="outlined"
          />
        </div>

        <div>
          <TextField
            id="search-Template-textfield-description"
            label={'Description'}
            type="text"
            sx={{ width: '220px' }}
            variant="outlined"
            value={searchTemplateDescription}
            onChange={handleSearchTemplateDescriptionChange}
            color="primary"
            autoComplete="off"
          />
        </div>

        <div>
          <Button
            onClick={handleResetSearchTemplateFilter}
            color="primary"
            sx={{
              paddingTop: '6px',
              paddingBottom: '6px',
              marginTop: '12px',
              width: '109px',
            }}
          >
            Reset Filter
          </Button>
        </div>
      </div>

      <SearchTemplatesTable
        isLoading={isInitialSearchTemplateLoading}
        data={mySearchTemplatesMapped ?? []}
        paginationModel={{
          page: searchTemplatePageNoAtomModel,
          pageSize: searchTemplatePageSizeAtomModel,
        }}
        onPaginationModelChange={(model) => {
          setSearchTemplatePageSizeAtomModel(model.pageSize);
          setSearchTemplatePageNoAtomModel(
            model.pageSize !== searchTemplatePageSizeAtomModel
              ? adjustPageNo(
                  searchTemplatePageSizeAtomModel,
                  searchTemplatePageNoAtomModel,
                  model.pageSize
                )
              : model.page
          );
        }}
        columnVisibilityModel={searchTemplateColumnVisibilityAtomModel}
        onColumnVisibilityModelChange={(newModel) => {
          setSearchTemplateColumnVisibilityAtomModel(newModel);
        }}
        sortModel={searchTemplateSortAtomModel}
        onSortModelChange={(newSortModel) => {
          setSearchTemplateSortAtomModel(newSortModel);
        }}
        activateDelete={setSearchTemplateForDelete}
        activateEdit={setParametersForTemplateEdit}
        activateUpdate={handleTemplateUpdate}
        activateCopy={handleTemplateCopy}
        activateCopyLink={activateCopyLink}
      />

      <AgDialog
        dialogTitle={'Delete this search template?'}
        isOpen={searchTemplateForDelete !== undefined}
        onClose={() => setSearchTemplateForDelete(undefined)}
        primaryButton={{
          text: 'Delete',
          onClick: handleDeleteSearchTemplateDialogDelete,
        }}
        secondaryButton={{
          text: 'Cancel',
          onClick: async () => setSearchTemplateForDelete(undefined),
        }}
      >
        <DialogContentText>
          Are you sure you want to delete the search template{' '}
          <strong>{searchTemplateForDelete?.name}</strong>?
          <br />
          <br />
          <em>Note: this action cannot be undone.</em>
        </DialogContentText>
      </AgDialog>

      <EditTemplateDialog
        isOpen={searchTemplateIdEdit !== undefined}
        onClose={() => setSearchTemplateIdEdit(undefined)}
        searchTemplateId={searchTemplateIdEdit || 0}
        editSearchTemplateName={searchTemplateNameEdit}
        setEditSearchTemplateName={setSearchTemplateNameEdit}
        editSearchTemplateDescription={searchTemplateDescriptionEdit}
        setEditSearchTemplateDescription={setSearchTemplateDescriptionEdit}
        originalSearchTemplateName={originalSearchTemplateName}
      />

      {searchTemplateForLoad && (
        <LoadSearchTemplateDialog
          searchTemplate={searchTemplateForLoad}
          onClose={handleCloseLoadTemplateModal}
        />
      )}

      <TemplateLoadErrorDialog
        isOpen={templateLoadError !== null}
        message={templateLoadError ?? ''}
        onClose={handleCloseTemplateLoadError}
      />
    </>
  );
}
